import type { ReactElement } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { OfflineScreen } from "@/components/OfflineScreen";
import { AuthProvider } from "@/context/AuthContext";
import { AdminRoute, ProtectedRoute, PublicOnlyRoute } from "@/components/ProtectedRoute";
import { AppLayout } from "@/components/AppLayout";
import { SECTIONS } from "@/lib/sections";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import SectionPage from "@/pages/SectionPage";
import AdminPage from "@/pages/AdminPage";
import AccountsPage from "@/pages/AccountsPage";
import MovementsPage from "@/pages/MovementsPage";
import BudgetPage from "@/pages/BudgetPage";
import ScheduledPage from "@/pages/ScheduledPage";
import MentorPage from "@/pages/MentorPage";
import DashboardPage from "@/pages/DashboardPage";
import SettingsPage from "@/pages/SettingsPage";
import SubscribePage from "@/pages/SubscribePage";
import WelcomePage from "@/pages/WelcomePage";
import { GoogleCallback } from "@/components/GoogleAuth";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import ConfirmEmailPage from "@/pages/ConfirmEmailPage";
import LegalPage from "@/pages/LegalPage";

const BUILT_PAGES: Record<string, ReactElement> = {
  cuentas: <AccountsPage />,
  movimientos: <MovementsPage />,
  presupuesto: <BudgetPage />,
  programados: <ScheduledPage />,
  mentor: <MentorPage />,
  resumen: <DashboardPage />,
};

function AppRoutes() {
  const location = useLocation();
  if (location.hash.includes("session_id=")) return <GoogleCallback hash={location.hash} />;
  return (
        <Routes>
          <Route element={<PublicOnlyRoute />}>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/registro" element={<RegisterPage />} />
            <Route path="/register" element={<Navigate to="/registro" replace />} />
          </Route>
          <Route path="/terminos" element={<LegalPage kind="terminos" />} />
          <Route path="/privacidad" element={<LegalPage kind="privacidad" />} />
          <Route path="/restablecer" element={<ResetPasswordPage />} />
          <Route path="/confirmar-correo" element={<ConfirmEmailPage />} />
          <Route element={<ProtectedRoute />}>
            <Route element={<AppLayout />}>
              {SECTIONS.map((s, i) => (
                <Route key={s.slug} path={s.path} element={BUILT_PAGES[s.slug] ?? <SectionPage section={s} index={i} />} />
              ))}
              <Route path="/categorias" element={<Navigate to="/configuracion#categorias" replace />} />
              <Route path="/configuracion" element={<SettingsPage />} />
              <Route path="/planes" element={<Navigate to="/suscripcion" replace />} />
              <Route path="/suscripcion" element={<SubscribePage />} />
              <Route path="/bienvenida" element={<WelcomePage />} />
              <Route path="/pago/exito" element={<WelcomePage />} />
              <Route element={<AdminRoute />}>
                <Route path="/admin" element={<AdminPage />} />
              </Route>
            </Route>
          </Route>
          <Route path="*" element={<Navigate to="/resumen" replace />} />
        </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
      <Toaster position="top-right" richColors />
      <OfflineScreen />
    </AuthProvider>
  );
}
